import React, { useState } from 'react';

/**
 * Structured Response Component
 * Renders AI responses with Markdown-like formatting and copy functionality
 */
export default function StructuredResponse({ content, fromCache }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Simple markdown-like rendering
  const renderContent = (text) => {
    // Split by sections
    const sections = text.split(/(?=📊|🔍|💡)/);

    return sections.map((section, idx) => {
      const lines = section.split('\n');

      return (
        <div key={idx} className="mb-4 last:mb-0">
          {lines.map((line, lineIdx) => {
            // Section headers
            if (line.startsWith('📊') || line.startsWith('🔍') || line.startsWith('💡')) {
              return (
                <div key={lineIdx} className="text-lg font-bold text-text-light dark:text-text-dark mb-2">
                  {line}
                </div>
              );
            }

            // Separator lines
            if (line.match(/^━+$/)) {
              return <div key={lineIdx} className="h-0.5 bg-border-light dark:bg-border-dark my-2" />;
            }

            // Bullet points
            if (line.startsWith('•')) {
              return (
                <div key={lineIdx} className="pl-2 my-1.5 text-text-light dark:text-text-dark">
                  {line}
                </div>
              );
            }

            // Numbered lists
            if (line.match(/^\d+\./)) {
              return (
                <div key={lineIdx} className="pl-2 my-1.5 font-medium text-text-light dark:text-text-dark">
                  {line}
                </div>
              );
            }

            // Regular text
            if (line.trim()) {
              return (
                <div key={lineIdx} className="my-1.5 text-text-light dark:text-text-dark">
                  {line}
                </div>
              );
            }

            return null;
          })}
        </div>
      );
    });
  };

  return (
    <div className="rounded-lg rounded-tl-none overflow-hidden">
      {/* Header */}
      <div className="flex justify-between items-center px-4 py-2 bg-primary/20">
        <div className="flex gap-2">
          {fromCache && (
            <span className="inline-block bg-green-500 text-white px-2 py-0.5 rounded text-xs font-semibold">
              Z cache
            </span>
          )}
        </div>
        <button
          className="px-3 py-1 bg-white/20 text-white border border-white/50 rounded hover:bg-white/30 transition-colors flex items-center gap-1.5 text-sm font-semibold"
          onClick={handleCopy}
          title="Zkopírovat odpověď"
        >
          {copied ? (
            <>
              <span className="material-symbols-outlined text-sm">check</span>
              Zkopírováno
            </>
          ) : (
            <>
              <span className="material-symbols-outlined text-sm">content_copy</span>
              Kopírovat
            </>
          )}
        </button>
      </div>

      {/* Content */}
      <div className="p-4 bg-background-light dark:bg-background-dark prose prose-sm dark:prose-invert max-w-none">
        {renderContent(content)}
      </div>
    </div>
  );
}
